import React, { useState } from 'react';
import { Project } from '@/entities/all';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Euro, TrendingUp, Building2, Calendar, Plus, Download, AlertCircle, Edit, Save, HandCoins, Trash2, CreditCard } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import FundingSourceForm from './FundingSourceForm';
import EncaissementForm from './EncaissementForm';

// Utilities pour générer des IDs uniques
const generateId = () => Math.random().toString(36).substr(2, 9);

const statusConfig = {
    draft_preparation: { label: 'Brouillon', color: 'bg-gray-100 text-gray-800' },
    submitted_review: { label: 'En instruction', color: 'bg-blue-100 text-blue-800' },
    approved_secured: { label: 'Sécurisé', color: 'bg-green-100 text-green-800' },
    rejected: { label: 'Rejeté', color: 'bg-red-100 text-red-800' },
    suspended: { label: 'Suspendu', color: 'bg-orange-100 text-orange-800' },
    cancelled: { label: 'Annulé', color: 'bg-gray-500 text-white' },
};

const sourceTypeLabels = {
    own_funds: 'Fonds propres',
    partner_contribution: 'Participation partenaire',
    bank_loan: 'Financement bancaire',
    public_subsidy: 'Subvention publique',
    leasing: 'Leasing financier'
};

const currencySymbols = {
    EUR: '€',
    USD: '$',
    MAD: 'DH'
};

// @ts-nocheck
const FinancementTab = ({ project, onProjectUpdate }) => {
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingSource, setEditingSource] = useState(null);
    const [isEditingBudget, setIsEditingBudget] = useState(false);
    const [newBudget, setNewBudget] = useState(project?.total_budget || 0);
    const [showEncaissementForm, setShowEncaissementForm] = useState(false);
    const [selectedSourceForPayment, setSelectedSourceForPayment] = useState(null);

    const formatCurrency = (value, currency) => {
        const symbol = currencySymbols[currency] || currency;
        if (!value && value !== 0) return `0 ${symbol}`;

        return new Intl.NumberFormat('fr-FR').format(value) + ' ' + symbol;
    };

    const fundingDetails = project?.funding_sources || [];
    const totalFunding = project?.total_budget || 0;
    const projectCurrency = project?.currency || 'EUR';

    const securedAmount = fundingDetails
        .filter(f => f.status === 'approved_secured')
        .reduce((sum, f) => sum + (f.amount || 0), 0);

    const totalEncaisse = fundingDetails
        .filter(f => f.status === 'approved_secured')
        .reduce((sum, f) => {
            const sourceEncaisse = (f.encaissements || []).reduce((encSum, enc) => encSum + (enc.amount || 0), 0);
            return sum + sourceEncaisse;
        }, 0);

    const fundingGap = Math.max(0, totalFunding - securedAmount);

    const handleSaveSource = async (data) => {
        try {
            let updatedSources;
            if (editingSource) {
                updatedSources = fundingDetails.map(source =>
                    source.id === editingSource.id ? { ...source, ...data } : source
                );
            } else {
                updatedSources = [...fundingDetails, { ...data, id: generateId() }];
            }

            await Project.update(project.id, { funding_sources: updatedSources });
            if (onProjectUpdate) {
                onProjectUpdate(project.id);
            }
            setIsFormOpen(false);
            setEditingSource(null);
        } catch (error) {
            console.error('Erreur lors de la sauvegarde:', error);
        }
    };

    const handleUpdateBudget = async () => {
        try {
            await Project.update(project.id, { total_budget: Number(newBudget) });
            if (onProjectUpdate) {
                onProjectUpdate(project.id);
            }
            setIsEditingBudget(false);
        } catch (error) {
            console.error('Erreur lors de la mise à jour du budget:', error);
        }
    };

    const handleEditSource = (source) => {
        setEditingSource(source);
        setIsFormOpen(true);
    };

    const handleNewSource = () => {
        setEditingSource(null);
        setIsFormOpen(true);
    };

    const handleDeleteSource = async (sourceId) => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer cette source de financement ?")) {
            try {
                const updatedSources = fundingDetails.filter(source => source.id !== sourceId);
                await Project.update(project.id, { funding_sources: updatedSources });
                if (onProjectUpdate) {
                    onProjectUpdate(project.id);
                }
            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
            }
        }
    };

    const handleDeclarePayment = (source) => {
        setSelectedSourceForPayment(source);
        setShowEncaissementForm(true);
    };

    const handleAddEncaissement = async (encaissementData) => {
        try {
            const updatedSources = fundingDetails.map(source => {
                if (source.id === selectedSourceForPayment.id) {
                    const currentEncaissements = source.encaissements || [];
                    return {
                        ...source,
                        encaissements: [...currentEncaissements, encaissementData]
                    };
                }
                return source;
            });

            await Project.update(project.id, { funding_sources: updatedSources });
            if (onProjectUpdate) {
                onProjectUpdate(project.id);
            }
            setShowEncaissementForm(false);
            setSelectedSourceForPayment(null);
        } catch (error) {
            console.error('Erreur lors de l\'ajout de l\'encaissement:', error);
        }
    };

    const getSourceEncaissements = (source) => {
        const encaissements = source.encaissements || [];
        const totalEncaisse = encaissements.reduce((sum, enc) => sum + (enc.amount || 0), 0);
        const progression = source.amount > 0 ? (totalEncaisse / source.amount) * 100 : 0;
        return { encaissements, totalEncaisse, progression };
    };

    return (
        <>
            {isFormOpen && (
                <FundingSourceForm
                    isOpen={isFormOpen}
                    onClose={() => {
                        setIsFormOpen(false);
                        setEditingSource(null);
                    }}
                    onSubmit={handleSaveSource}
                    source={editingSource}
                    projectCurrency={projectCurrency}
                />
            )}

            {showEncaissementForm && selectedSourceForPayment && (
                <EncaissementForm
                    isOpen={showEncaissementForm}
                    onClose={() => {
                        setShowEncaissementForm(false);
                        setSelectedSourceForPayment(null);
                    }}
                    onSubmit={handleAddEncaissement}
                    projectCurrency={projectCurrency}
                    maxAmount={selectedSourceForPayment.amount - getSourceEncaissements(selectedSourceForPayment).totalEncaisse}
                />
            )}

            <div className="space-y-8">
                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-50 to-teal-100">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium text-teal-600">Budget Global</CardTitle>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="w-6 h-6"
                                onClick={() => setIsEditingBudget(!isEditingBudget)}
                            >
                                <Edit className="w-4 h-4 text-teal-600" />
                            </Button>
                        </CardHeader>
                        <CardContent>
                            {isEditingBudget ? (
                                <div className="flex items-center gap-2">
                                    <Input
                                        type="number"
                                        value={newBudget}
                                        onChange={(e) => setNewBudget(e.target.value)}
                                        className="text-2xl font-bold p-1 h-10"
                                    />
                                    <Button size="icon" onClick={handleUpdateBudget} className="w-8 h-8">
                                        <Save className="w-4 h-4" />
                                    </Button>
                                </div>
                            ) : (
                                <p className="text-3xl font-bold text-teal-900">
                                    {formatCurrency(totalFunding, projectCurrency)}
                                </p>
                            )}
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-green-100">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium text-green-600">Montant Sécurisé</CardTitle>
                            <HandCoins className="w-5 h-5 text-green-600" />
                        </CardHeader>
                        <CardContent>
                            <p className="text-3xl font-bold text-green-900">
                                {formatCurrency(securedAmount, projectCurrency)}
                            </p>
                            <Progress
                                value={totalFunding > 0 ? (securedAmount / totalFunding) * 100 : 0}
                                className="mt-2"
                                indicatorClassName="bg-green-500"
                            />
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium text-blue-600">Total Encaissé</CardTitle>
                            <CreditCard className="w-5 h-5 text-blue-600" />
                        </CardHeader>
                        <CardContent>
                            <p className="text-3xl font-bold text-blue-900">
                                {formatCurrency(totalEncaisse, projectCurrency)}
                            </p>
                            <Progress
                                value={securedAmount > 0 ? (totalEncaisse / securedAmount) * 100 : 0}
                                className="mt-2"
                                indicatorClassName="bg-blue-500"
                            />
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg bg-gradient-to-br from-red-50 to-red-100">
                        <CardHeader className="flex flex-row items-center justify-between pb-2">
                            <CardTitle className="text-sm font-medium text-red-600">Écart à combler</CardTitle>
                            <AlertCircle className="w-5 h-5 text-red-600" />
                        </CardHeader>
                        <CardContent>
                            <p className="text-3xl font-bold text-red-900">
                                {formatCurrency(fundingGap, projectCurrency)}
                            </p>
                        </CardContent>
                    </Card>
                </div>

                {/* Funding Sources Table */}
                <Card className="border-0 shadow-lg">
                    <CardHeader className="flex flex-row items-center justify-between">
                        <CardTitle className="text-xl font-bold text-gray-900">Sources de financement</CardTitle>
                        <Button variant="outline" className="gap-2" onClick={handleNewSource}>
                            <Plus className="w-4 h-4" /> Nouvelle source
                        </Button>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Source</TableHead>
                                    <TableHead>Montant</TableHead>
                                    <TableHead>Statut</TableHead>
                                    <TableHead>Progression</TableHead>
                                    <TableHead>Contact</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {fundingDetails.map((funding) => {
                                    const { encaissements, totalEncaisse, progression } = getSourceEncaissements(funding);

                                    return (
                                        <TableRow key={funding.id} className="hover:bg-gray-50">
                                            <TableCell>
                                                <p className="font-medium text-gray-900">
                                                    {sourceTypeLabels[funding.source_type] || funding.source_type}
                                                </p>
                                                <p className="text-sm text-gray-500">
                                                    {funding.bank_name || funding.subsidy_program || funding.leasing_company}
                                                </p>
                                            </TableCell>
                                            <TableCell className="font-semibold">
                                                {formatCurrency(funding.amount, projectCurrency)}
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={statusConfig[funding.status]?.color || 'bg-gray-100 text-gray-800'}>
                                                    {statusConfig[funding.status]?.label || 'Inconnu'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {funding.status === 'approved_secured' ? (
                                                    <div className="space-y-1">
                                                        <div className="flex justify-between text-xs">
                                                            <span>{encaissements.length} encaissement{encaissements.length > 1 ? 's' : ''}</span>
                                                            <span>{progression.toFixed(1)}%</span>
                                                        </div>
                                                        <Progress value={progression} className="h-1.5" />
                                                        <p className="text-xs text-gray-600">
                                                            {formatCurrency(totalEncaisse, projectCurrency)} / {formatCurrency(funding.amount, projectCurrency)}
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <p className="font-medium">{funding.contact_person || '—'}</p>
                                                <p className="text-sm text-gray-500">{funding.contact_email || ''}</p>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-1">
                                                    <Button variant="ghost" size="icon" onClick={() => handleEditSource(funding)}>
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    {funding.status === 'approved_secured' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleDeclarePayment(funding)}
                                                            className="text-blue-600 hover:text-blue-700"
                                                            title="Déclarer un encaissement"
                                                        >
                                                            <CreditCard className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="text-red-500 hover:text-red-700"
                                                        onClick={() => handleDeleteSource(funding.id)}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>

                        {fundingDetails.length === 0 && (
                            <div className="text-center py-8">
                                <p className="text-gray-500">Aucune source de financement définie</p>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </>
    );
};

export default FinancementTab;
